import { useEffect, useRef, useState } from "react";
import { Routes, Route } from "react-router-dom";
import TonalAudiometry from "./pages/tonal-audiometry/tonalAudiometry";
import VerbalAudiometry from "./pages/verbal-audiometry/verbalAudiometry";
import Audiometer from "./pages/audiometer/audiometer";
import TopBar from "./pages/header/header";
import ConfirmEmail from "./pages/confirm-email/confirmEmail";
import Login from "./pages/login/login";
import NotFound from "./pages/not-found/notFound";
import ResetPassword from "./pages/reset-password/resetPassword";
import UserResults from "./pages/user-results/userResults";
import UserSettings from "./pages/user-settings/userSettings";
import UserActivity from "./pages/user-activity/userActivity";
import PrivacyPolicy from "./pages/privacy-policy/privacyPolicy";
import { fetchUser, getUser } from "./auth/auth";
import { I18nProvider } from "./utils/translate/i18n/i18n";
import { logActivity } from "./utils/logActivity";
import InfoModal from "./modal/infoModal/infoModal";
import Footer from "./pages/footer/footer";
import type { UserModel } from "./type/user";
import './App.scss'
import "./style/common.scss";

//const INFO_MODAL_LAST_SHOWN_KEY = "info-modal-last-shown-date";

// const getTodayKey = () => {
//   const today = new Date();
//   const year = today.getFullYear();
//   const month = String(today.getMonth() + 1).padStart(2, "0");
//   const day = String(today.getDate()).padStart(2, "0");

//   return `${year}-${month}-${day}`;
// };

// const shouldShowInfoModalOnStartup = () => {
//   return localStorage.getItem(INFO_MODAL_LAST_SHOWN_KEY) !== getTodayKey();
// };

const applyDarkMode = (user: UserModel | null) => {
  const isDark = user?.parameters?.dark_mode?.value === true;
  document.body.classList.toggle('dark-mode', isDark);
  document.body.classList.toggle("light-mode", !isDark);
};


const App: React.FC = () => {
  const [user, setUser] = useState<UserModel | null>(() => getUser());
  const [showInfoModal, setShowInfoModal] = useState(false);

  const handleCloseInfoModal = () => {
    setShowInfoModal(false);
  };

  useEffect(() => {
    applyDarkMode(user);
  }, [user]);

  const launchLogged = useRef(false);
  useEffect(() => {
    if (launchLogged.current) return;
    launchLogged.current = true;
    fetchUser();
    logActivity('launch_app');
  }, []);

  useEffect(() => {
    const syncUser = () => setUser(getUser());
    window.addEventListener("auth-change", syncUser);
    return () => window.removeEventListener("auth-change", syncUser);
  }, []);

  useEffect(() => {
    const openModal = () => setShowInfoModal(true);
    window.addEventListener("show-info-modal", openModal);
    return () => window.removeEventListener("show-info-modal", openModal);
  }, []);

  return (
    <I18nProvider>
      <div className="app-shell">
        <TopBar />
        <InfoModal open={showInfoModal} onClose={handleCloseInfoModal} />
        <main className="app-content">
          <Routes>
            <Route path="/" element={<TonalAudiometry />} />
            <Route path="/tonal-audiometry" element={<TonalAudiometry />} />              
            {user && <Route path="/verbal-audiometry" element={<VerbalAudiometry />} />}
            <Route path="/audiometer" element={<Audiometer />} />
            <Route path="/confirm-email" element={<ConfirmEmail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/user-results" element={<UserResults />} />
            <Route path="/user-settings" element={<UserSettings />} />
            {user?.admin === true && (
              <Route path="/user-activity" element={<UserActivity />} />
            )}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </I18nProvider>
  )
};

export default App
